import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import MediaThumb from './MediaThumb';
import StatusPill from './StatusPill';
import DesignerAssignControl from './DesignerAssignControl';
import ContentWriterAssignControl from './ContentWriterAssignControl';
import ThreadPanel from '../../components/ThreadPanel';
import { Toast } from '../../../../../shared/Toast';
import { StatusChip } from '../../../../creatives/creativeStatus';
import useCreativeRequests from '../hooks/useCreativeRequests';
import useAdminCreativeThread from '../hooks/useAdminCreativeThread';
import useCreativeVersions from '../hooks/useCreativeVersions';
import {
  VARIATION_FIELDS,
  canApprove,
  formatEpoch,
  isAssigned,
  isVariationFilled,
  isVideoSubType,
  subTypeLabel,
} from '../config';

/**
 * One segmented control, four views. Module level so the array identity is
 * stable and can never leak into a dependency list.
 *
 * "Chat" leads because the primary thing CST does on a creative is talk it
 * through — discussing and reviewing the draft with the content writer and the
 * sales person is the action this screen is opened for. The admin panel reads
 * that thread from the same shared `/logs` endpoint the requests come from, so
 * it is a first-class view here just like on the writer panel.
 *
 * "Requests" is the old "Pending" tab, relabeled only: it still combines the
 * submitted content draft with any creative-type/design-change requests, since
 * both are "something needs your decision on this creative" and splitting them
 * into two separately-triggered tabs never helped.
 *
 * "Info" (client / sales person / design / assignments) and "Media" (the
 * designer's current live creative, read-only) follow as reference views.
 */
const VIEWS = [
  { key: 'chat', label: 'Chat', icon: 'message-circle' },
  { key: 'requests', label: 'Requests', icon: 'bell' },
  { key: 'info', label: 'Info', icon: 'info' },
  { key: 'media', label: 'Media', icon: 'image' },
  { key: 'versions', label: 'Versions', icon: 'history' },
];

const DEFAULT_VIEW = 'chat';

/** Log shapes written by the salesperson-facing endpoints in
 * way2news_ads_order_booking_api into the shared edit-logs table. */
const REQUEST_LOG_TYPES = ['creative_type_request', 'design_change_request'];

/** `changes` arrives as jsonb — usually an array already, occasionally a JSON string. */
const parseLogChanges = (changes) => {
  if (!changes) return [];
  if (Array.isArray(changes)) return changes;
  if (typeof changes === 'string') {
    try {
      const parsed = JSON.parse(changes);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

/**
 * This panel does not own `creative_type`'s label set (no CREATIVE_TYPE_LABELS
 * source exists anywhere in this codebase yet) — only `sub_type` has an
 * established one (SUB_TYPE_LABELS / subTypeLabel), so `requested_type` renders
 * as the raw value sent by the salesperson endpoint, never a guessed label.
 */
const describeRequest = (change) => {
  if (change?.type === 'creative_type_request') {
    const typeLabel = change.requested_type || 'unknown';
    const subLabel = change.requested_sub_type ? ` (${subTypeLabel(change.requested_sub_type)})` : '';
    return `Requested creative type change to ${typeLabel}${subLabel}`;
  }
  if (change?.type === 'design_change_request') {
    return 'Requested a design change';
  }
  return null;
};

/**
 * `versions[].createdAt` is a NEW/different backend contract (this repo's
 * other `created_time` fields are all epoch seconds, handled by `formatEpoch`
 * from `../config`) — it may or may not be epoch seconds, so this is handled
 * defensively: reuse `formatEpoch` when it looks like epoch seconds, otherwise
 * fall back to parsing it as an ISO/date string, guarded by a validity check.
 */
const formatVersionTime = (createdAt) => {
  if (createdAt == null || createdAt === '') return '—';
  if (typeof createdAt === 'number' && createdAt > 1e9 && createdAt < 1e11) {
    return formatEpoch(createdAt);
  }
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const MetaRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-medium text-slate-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm text-slate-800">{value || '—'}</span>
  </div>
);

const DraftField = ({ label, value }) =>
  value ? (
    <div className="text-xs">
      <span className="font-semibold text-slate-500">{label}: </span>
      <span className="whitespace-pre-wrap break-words text-slate-700">{value}</span>
    </div>
  ) : null;

/**
 * The creative's CURRENT live media — read-only reference. Copied from the
 * content writer panel's drawer: media belongs to the designer, and neither
 * the writer nor this admin screen can change it. The only addition is
 * `onZoom` — on the still image directly, and on an overlay button for the
 * video — which drives the drawer's ONE full-screen overlay, the same one the
 * Chat tab's attachments feed.
 */
const MediaPreview = ({ row, onZoom }) => {
  const [imgBroken, setImgBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const video = isVideoSubType(row?.sub_type);

  useEffect(() => {
    setImgBroken(false);
    setVideoBroken(false);
  }, [row?.img_urls, row?.video_urls]);

  const hasVideo = video && !!row?.video_urls && !videoBroken;
  const hasImage = !!row?.img_urls && !imgBroken;
  const stored = !!(row?.img_urls || row?.video_urls);

  if (!hasVideo && !hasImage) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
        <SlIcon
          library="lucide"
          name={video ? 'video-off' : 'image-off'}
          className="mb-2 text-slate-300"
          style={{ fontSize: '24px' }}
        />
        <p className="text-sm font-medium text-slate-600">
          {stored ? 'Preview unavailable' : 'Nothing uploaded yet'}
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {stored
            ? 'The stored file could not be loaded.'
            : 'The designer has not uploaded a design for this creative yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {hasVideo ? (
        // The zoom trigger is a separate overlay button, never an onClick on
        // the <video> itself — that would fight the native playback controls.
        <div className="relative">
          <video
            src={row.video_urls}
            poster={hasImage ? row.img_urls : undefined}
            controls
            onError={() => setVideoBroken(true)}
            className="max-h-72 w-full rounded-xl border border-slate-200 bg-black"
          />
          {typeof onZoom === 'function' && (
            <button
              type="button"
              onClick={() => onZoom(row.video_urls, true)}
              aria-label="Expand video"
              title="Expand video"
              className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
            >
              <SlIcon library="lucide" name="maximize-2" style={{ fontSize: '14px' }} />
            </button>
          )}
        </div>
      ) : (
        <img
          src={row.img_urls}
          alt={row.creative_name || 'creative'}
          onError={() => setImgBroken(true)}
          onClick={typeof onZoom === 'function' ? () => onZoom(row.img_urls, false) : undefined}
          className={`max-h-72 w-full rounded-xl border border-slate-200 bg-slate-50 object-contain ${
            typeof onZoom === 'function' ? 'cursor-zoom-in' : ''
          }`}
        />
      )}
      {hasVideo && hasImage && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2">
          <MediaThumb src={row.img_urls} alt="Thumbnail" className="h-12 w-12" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700">Thumbnail</p>
            <p className="text-[11px] text-slate-400">Shown before the video plays</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Read/review drawer for the CST admin panel (full screen below `sm`).
 *
 * The `row` prop still carries everything the review needs (the full
 * `variations` array included — there is no `/detail` call), but the thread
 * and the request log both come from the shared `/logs` endpoint via their own
 * drawer-scoped hooks. Every mutation other than messaging is raised to
 * `BookingCreativesAssign`, which owns the confirm dialogs, the API calls and
 * the row patching; this component only decides WHAT can be raised, from
 * `row.content_status`.
 */
const DetailDrawer = ({
  open,
  row,
  onClose,
  onRequestApprove,
  onRequestReject,
  designers = [],
  designerAssignLocked = false,
  onAssignDesigner,
  writers = [],
  writersLoading = false,
  onAssignWriter,
  busy = false,
}) => {
  const bscId = row?.bsc_id ?? null;
  const [view, setView] = useState(DEFAULT_VIEW);
  const [zoom, setZoom] = useState(null);
  // Index INTO `row.variations` (not into the filtered list) of the draft the
  // reviewer picked to approve. See `drafts` below.
  const [selectedIndex, setSelectedIndex] = useState(null);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setView(DEFAULT_VIEW);
    setZoom(null);
    setSelectedIndex(null);
  }, [open, bscId]);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const body = document.body;
    const outlet = document.querySelector('.outlet-body');
    const prevBody = body.style.overflow;
    const prevOutlet = outlet ? outlet.style.overflow : '';
    body.style.overflow = 'hidden';
    if (outlet) outlet.style.overflow = 'hidden';
    const timer = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      body.style.overflow = prevBody;
      if (outlet) outlet.style.overflow = prevOutlet;
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  const closeZoom = useCallback(() => setZoom(null), []);
  const handleZoom = useCallback((url, isVideo) => setZoom({ url, isVideo }), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (zoom) {
        e.stopPropagation();
        closeZoom();
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, zoom, closeZoom, onClose]);

  /**
   * `{ variation, index }` pairs, keeping the ORIGINAL position in
   * `row.variations`. Filtering first and using the filtered position would
   * send the wrong variation index to /approve whenever an empty entry sits
   * earlier in the array.
   */
  const drafts = useMemo(() => {
    const list = Array.isArray(row?.variations) ? row.variations : [];
    return list
      .map((variation, index) => ({ variation, index }))
      .filter((item) => isVariationFilled(item.variation));
  }, [row?.variations]);

  const {
    logs: threadLogs,
    loading: threadLoading,
    error: threadError,
    reload: reloadThread,
    sendMessage,
    sendAttachment,
    mergeIncomingLogs,
  } = useAdminCreativeThread(bscId, { enabled: open });

  const {
    error: requestsMutateError,
    approve: approveRequestLog,
    approvingId,
    reject: rejectRequestLog,
    rejectingId,
  } = useCreativeRequests(bscId, threadLogs, mergeIncomingLogs);

  // Requests are derived from the SAME log feed the Chat tab fetches (see
  // useCreativeRequests's header comment) — no second `/logs` call, so
  // "loading"/"error" here just mirror the thread fetch, plus a mutate-only
  // error from a failed approve/reject.
  const requestLogs = threadLogs;
  const requestsLoading = threadLoading;
  const requestsError = requestsMutateError || threadError;

  // Lazy — scoped to the Versions tab itself (not just `open`, like the other
  // hooks above): version history isn't needed until the tab is actually
  // viewed.
  const {
    versions,
    loading: versionsLoading,
    error: versionsError,
    reload: reloadVersions,
  } = useCreativeVersions(bscId, { enabled: open && view === 'versions' });

  // Current/newest first — sorted client-side defensively, never assuming the
  // backend already orders them: isCurrent first, then version descending.
  const sortedVersions = useMemo(() => {
    const list = Array.isArray(versions) ? versions : [];
    return [...list].sort((a, b) => {
      if (!!a?.isCurrent !== !!b?.isCurrent) return a?.isCurrent ? -1 : 1;
      return Number(b?.version || 0) - Number(a?.version || 0);
    });
  }, [versions]);

  const requestEntries = useMemo(() => {
    const list = Array.isArray(requestLogs) ? requestLogs : [];
    const approvedBy = new Map();
    const rejectedBy = new Map();
    list.forEach((log) => {
      const change = parseLogChanges(log?.changes)[0];
      const id = Number(change?.request_bscel_id);
      if (!Number.isFinite(id)) return;
      if (change?.type === 'request_approved') {
        approvedBy.set(id, { name: log.user_name, at: log.created_time });
      } else if (change?.type === 'request_rejected') {
        rejectedBy.set(id, { name: log.user_name, at: log.created_time });
      }
    });
    return list
      .map((log) => ({ log, change: parseLogChanges(log?.changes)[0] }))
      .filter((item) => REQUEST_LOG_TYPES.includes(item.change?.type))
      .map((item) => ({
        ...item,
        approval: approvedBy.get(Number(item.log.id)) || null,
        rejection: rejectedBy.get(Number(item.log.id)) || null,
      }));
  }, [requestLogs]);

  const pendingRequestCount = requestEntries.filter(
    (item) => !item.approval && !item.rejection
  ).length;
  // A submitted draft is one pending decision (approve or reject the batch),
  // not one per variation — only counts while there is actually a decision to
  // make (status 3, Submitted). Combined with pending requests for the tab badge.
  const pendingDraftCount = canApprove(row?.content_status) && drafts.length > 0 ? 1 : 0;
  const totalPendingCount = pendingDraftCount + pendingRequestCount;

  const handleApproveRequest = useCallback(
    async (requestLogId) => {
      const result = await approveRequestLog(requestLogId);
      if (result === true) Toast('success', 'Request approved');
      else Toast('error', result || 'Unable to approve this request.');
    },
    [approveRequestLog]
  );

  const handleRejectRequest = useCallback(
    async (requestLogId) => {
      const result = await rejectRequestLog(requestLogId);
      if (result === true) Toast('success', 'Request rejected');
      else Toast('error', result || 'Unable to reject this request.');
    },
    [rejectRequestLog]
  );

  if (!open || !row) return null;

  const status = row.content_status;
  const reviewable = canApprove(status);
  const assigned = isAssigned(row);
  const hasMedia = !!(row.img_urls || row.video_urls);
  const writerName =
    row.content_writer_name || (row.content_writer_id ? `#${row.content_writer_id}` : '');
  const designerName = row.designer_name || (row.designer_id ? `#${row.designer_id}` : '');

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Creative ${row.creative_name || row.bsc_id}`}
        tabIndex={-1}
        className="flex h-full w-full flex-col bg-white shadow-2xl outline-none sm:max-w-xl"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold text-slate-900">
                {row.creative_name || `Creative #${row.bsc_id}`}
              </h2>
              <StatusPill status={status} />
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              #{row.bsc_id} · {subTypeLabel(row.sub_type)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '18px' }} />
          </button>
        </header>

        <div
          role="tablist"
          aria-label="Creative views"
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-2 py-2"
        >
          {VIEWS.map((v) => {
            const active = view === v.key;
            const badge =
              v.key === 'requests' && totalPendingCount ? String(totalPendingCount) : null;
            return (
              <button
                key={v.key}
                type="button"
                role="tab"
                id={`cst-drawer-tab-${v.key}`}
                aria-selected={active}
                aria-controls={`cst-drawer-panel-${v.key}`}
                onClick={() => setView(v.key)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                  active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <SlIcon library="lucide" name={v.icon} style={{ fontSize: '14px' }} />
                {v.label}
                {badge && (
                  <span
                    className={`rounded-full px-1.5 text-[10px] font-semibold ${
                      active ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {badge}
                  </span>
                )}
                {v.key === 'media' && hasMedia && (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                    title="Media is attached"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Draft-level Reject only, and only while a draft is actually
            submitted — so the whole bar is conditional, otherwise it would
            render as a bare empty bordered strip. Assign now lives in the Info
            tab's Content writer / Designer sections, next to who is assigned.
            Approve is NOT here either — it needs a chosen draft, so it lives at
            the bottom of the Drafts list. */}
        {reviewable && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => onRequestReject(row)}
              className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SlIcon library="lucide" name="x-circle" style={{ fontSize: '14px' }} />
              Reject
            </button>
          </div>
        )}

        <div
          role="tabpanel"
          id={`cst-drawer-panel-${view}`}
          aria-labelledby={`cst-drawer-tab-${view}`}
          className="min-h-0 flex-1"
        >
          {/* No `canSubmitDrafts` / `onOpenSubmit`: CST reviews drafts, it never
              submits them, and ThreadPanel hides the submit button when either
              is missing. Messaging itself has no status gate on either panel. */}
          {view === 'chat' && (
            <div className="h-full">
              <ThreadPanel
                logs={threadLogs}
                loading={threadLoading}
                error={threadError}
                onRetry={reloadThread}
                canSend
                onSend={sendMessage}
                onSendAttachment={sendAttachment}
                onZoom={handleZoom}
              />
            </div>
          )}

          {view === 'requests' && (
            <div className="h-full space-y-5 overflow-y-auto bg-slate-50 px-4 py-3">
              {/* Nothing pending at all — neither a draft nor a request. */}
              {drafts.length === 0 &&
                !requestsLoading &&
                !requestsError &&
                requestEntries.length === 0 && (
                  <div className="px-4 py-10 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                      <SlIcon
                        library="lucide"
                        name="check-circle"
                        className="text-slate-400"
                        style={{ fontSize: '22px' }}
                      />
                    </div>
                    <p className="text-sm font-medium text-slate-700">Nothing pending</p>
                    <p className="mx-auto mt-1 max-w-xs text-xs text-slate-500">
                      {assigned
                        ? 'No draft submitted and no open requests from the sales person for this creative.'
                        : 'Assign a content writer and their drafts will show up here for review.'}
                    </p>
                  </div>
                )}

              {/* Content Draft — a submitted draft is one pending decision:
                  approve the chosen variation, or reject the whole batch (the
                  Reject button lives in the action bar above). */}
              {drafts.length > 0 && (
                <section aria-label="Content draft">
                  <div className="mb-2 flex items-center gap-1.5">
                    <SlIcon
                      library="lucide"
                      name="file-text"
                      className="text-slate-400"
                      style={{ fontSize: '14px' }}
                    />
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Content Draft
                    </p>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {drafts.length} draft{drafts.length === 1 ? '' : 's'} submitted
                    {reviewable ? ' · pick the one to approve' : ''}
                  </p>
                  {/* Radio semantics only while a decision is possible. At any
                      other status this is a plain read-only list — and at
                      status 5 (Approved) nothing is marked as the winner,
                      because the API returns no field saying which variation
                      was approved. */}
                  <ol
                    className="space-y-3"
                    role={reviewable ? 'radiogroup' : undefined}
                    aria-label={reviewable ? 'Submitted drafts' : undefined}
                  >
                    {drafts.map(({ variation, index }, position) => {
                      const selected = selectedIndex === index;
                      const tabbable = selected || (selectedIndex === null && position === 0);
                      const selectable = reviewable && !busy;
                      return (
                        <li
                          key={index}
                          role={reviewable ? 'radio' : undefined}
                          aria-checked={reviewable ? selected : undefined}
                          tabIndex={reviewable ? (tabbable ? 0 : -1) : undefined}
                          onClick={selectable ? () => setSelectedIndex(index) : undefined}
                          onKeyDown={
                            selectable
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedIndex(index);
                                  }
                                }
                              : undefined
                          }
                          className={`rounded-xl border bg-white p-3 transition focus:outline-none ${
                            reviewable
                              ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-sky-400'
                              : ''
                          } ${
                            selected
                              ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="mb-1.5 flex items-center gap-2">
                            {reviewable && (
                              <span
                                aria-hidden="true"
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                  selected ? 'border-sky-600' : 'border-slate-300'
                                }`}
                              >
                                {selected && (
                                  <span className="h-2 w-2 rounded-full bg-sky-600" />
                                )}
                              </span>
                            )}
                            <span className="text-sm font-semibold text-slate-900">
                              Draft {position + 1}
                            </span>
                            <span className="flex items-center gap-1">
                              {VARIATION_FIELDS.map((f) => {
                                const filled = !!String(variation?.[f.key] || '').trim();
                                return (
                                  <span
                                    key={f.key}
                                    title={`${f.label}${filled ? ' set' : ' empty'}`}
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      filled ? 'bg-sky-500' : 'bg-slate-200'
                                    }`}
                                  />
                                );
                              })}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {VARIATION_FIELDS.map((f) => (
                              <DraftField
                                key={f.key}
                                label={f.label}
                                value={variation?.[f.key]}
                              />
                            ))}
                          </div>
                        </li>
                      );
                    })}
                  </ol>

                  {reviewable && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <button
                        type="button"
                        disabled={busy || selectedIndex === null}
                        onClick={() => onRequestApprove(row, selectedIndex)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <SlIcon library="lucide" name="check" style={{ fontSize: '15px' }} />
                        Approve selected draft
                      </button>
                      {selectedIndex === null && (
                        <p className="mt-1.5 text-center text-xs text-slate-500">
                          Select a draft above to approve it.
                        </p>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* Requests — creative-type/design-change requests from the sales
                  person. Only shown when there's something to say (loading, an
                  error, or actual entries) — an empty Requests section with
                  nothing under it would just be clutter next to the unified
                  empty state above. */}
              {(requestsLoading || requestsError || requestEntries.length > 0) && (
                <section aria-label="Requests" className="space-y-3">
                  <div className="flex items-center gap-1.5">
                    <SlIcon
                      library="lucide"
                      name="bell"
                      className="text-slate-400"
                      style={{ fontSize: '14px' }}
                    />
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Requests
                    </p>
                  </div>

                  {requestsLoading && requestEntries.length === 0 && (
                    <div className="space-y-3 py-4">
                      {[0, 1].map((i) => (
                        <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                      ))}
                    </div>
                  )}

                  {requestsError && requestEntries.length === 0 && !requestsLoading && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-center">
                      <p className="text-sm text-rose-800">{requestsError}</p>
                    </div>
                  )}

                  {requestEntries.map(({ log, change, approval, rejection }) => (
                    <div key={log.id} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">{describeRequest(change)}</p>
                          {change?.type === 'design_change_request' && change?.note && (
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">
                              {change.note}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-slate-400">
                            {log.user_name || 'Sales person'} · {formatEpoch(log.created_time)}
                          </p>
                        </div>
                        {approval ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            <SlIcon library="lucide" name="check" style={{ fontSize: '12px' }} />
                            Approved{approval.name ? ` by ${approval.name}` : ''}
                          </span>
                        ) : rejection ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
                            <SlIcon library="lucide" name="x" style={{ fontSize: '12px' }} />
                            Rejected{rejection.name ? ` by ${rejection.name}` : ''}
                          </span>
                        ) : (
                          /* Either decision in flight disables BOTH buttons —
                             the two endpoints write to the same request row. */
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              disabled={approvingId != null || rejectingId != null}
                              onClick={() => handleApproveRequest(log.id)}
                              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {approvingId === log.id ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                              ) : (
                                <SlIcon library="lucide" name="check" style={{ fontSize: '12px' }} />
                              )}
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={approvingId != null || rejectingId != null}
                              onClick={() => handleRejectRequest(log.id)}
                              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {rejectingId === log.id ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-rose-300 border-t-rose-700" />
                              ) : (
                                <SlIcon
                                  library="lucide"
                                  name="x-circle"
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>
          )}

          {view === 'info' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <SlIcon
                    library="lucide"
                    name="building-2"
                    className="text-slate-400"
                    style={{ fontSize: '13px' }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Client
                  </p>
                </div>
                <div className="divide-y divide-slate-200">
                  <MetaRow label="Brand" value={row.brand_name} />
                  <MetaRow label="Company" value={row.client_name} />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <SlIcon
                    library="lucide"
                    name="user-round"
                    className="text-slate-400"
                    style={{ fontSize: '13px' }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Sales person
                  </p>
                </div>
                <div className="divide-y divide-slate-200">
                  <MetaRow
                    label="Sales person"
                    value={
                      row.sales_person_name
                        ? `${row.sales_person_name}${row.sales_person_id ? ` (#${row.sales_person_id})` : ''}`
                        : row.sales_person_id
                          ? `#${row.sales_person_id}`
                          : '—'
                    }
                  />
                  <MetaRow
                    label="Assigned by"
                    value={
                      row.assigned_by_name || (row.assigned_by_id ? `#${row.assigned_by_id}` : '—')
                    }
                  />
                  <MetaRow label="Assigned time" value={formatEpoch(row.assigned_time)} />
                  <MetaRow label="Created" value={formatEpoch(row.created_time)} />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <SlIcon
                    library="lucide"
                    name="palette"
                    className="text-slate-400"
                    style={{ fontSize: '13px' }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Design / Creative
                  </p>
                </div>
                <div className="divide-y divide-slate-200">
                  <MetaRow label="Creative type" value={row.creative_type} />
                  {/* The creative's own DESIGN status (booking_slot_creatives.status),
                      NOT the content-writer workflow status below — the two must
                      never share this "Current status" label. */}
                  <div className="flex items-center justify-between gap-4 py-2">
                    <span className="shrink-0 text-xs font-medium text-slate-500">
                      Design status
                    </span>
                    <StatusChip status={row.status} />
                  </div>
                </div>
              </div>

              {/* Designer before Content writer: the design is assigned first,
                  the copy is written against it. Violet avatar so the two
                  assignment sections are distinguishable at a glance. */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <SlIcon
                    library="lucide"
                    name="user-check"
                    className="text-slate-400"
                    style={{ fontSize: '13px' }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Designer
                  </p>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  {designerName ? (
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100">
                        <SlIcon
                          library="lucide"
                          name="user-check"
                          className="text-violet-700"
                          style={{ fontSize: '15px' }}
                        />
                      </span>
                      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-slate-800">
                        {designerName}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm italic text-slate-400">No designer assigned yet</p>
                  )}
                  <DesignerAssignControl
                    row={row}
                    designers={designers}
                    locked={designerAssignLocked}
                    onAssign={onAssignDesigner}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <SlIcon
                    library="lucide"
                    name="user-pen"
                    className="text-slate-400"
                    style={{ fontSize: '13px' }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Content writer
                  </p>
                </div>
                {/* The content-writer WORKFLOW status (row.content_status), NOT the
                    creative's own design status above — the two must never share
                    the "Current status" label. */}
                <div className="flex items-center justify-between gap-4 py-2">
                  <span className="shrink-0 text-xs font-medium text-slate-500">
                    Content status
                  </span>
                  <StatusPill status={status} size="md" />
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  {assigned && writerName ? (
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100">
                        <SlIcon
                          library="lucide"
                          name="user-pen"
                          className="text-sky-700"
                          style={{ fontSize: '15px' }}
                        />
                      </span>
                      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-slate-800">
                        {writerName}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm italic text-slate-400">No content writer assigned yet</p>
                  )}
                  <ContentWriterAssignControl
                    row={row}
                    writers={writers}
                    writersLoading={writersLoading}
                    onAssign={onAssignWriter}
                  />
                </div>
              </div>

              {row.instructions && (
                <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Instructions
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
                    {row.instructions}
                  </p>
                </div>
              )}

              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Current live content (read-only)
                </p>
                <div className="divide-y divide-slate-200">
                  <MetaRow label="Headline" value={row.head_line} />
                  <MetaRow label="Description" value={row.description} />
                  <MetaRow label="Link text" value={row.text_in_link} />
                </div>
              </div>
            </div>
          )}

          {view === 'media' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <MediaPreview row={row} onZoom={handleZoom} />
              <p className="flex items-start gap-1.5 text-xs text-slate-500">
                <SlIcon
                  library="lucide"
                  name="info"
                  className="mt-[1px] shrink-0"
                  style={{ fontSize: '13px' }}
                />
                <span>
                  This is the designer&apos;s current live media, shown for reference only. It
                  cannot be changed from this screen — you review the written drafts here.
                </span>
              </p>
            </div>
          )}

          {view === 'versions' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              {versionsLoading && sortedVersions.length === 0 && (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />
                  ))}
                </div>
              )}

              {versionsError && sortedVersions.length === 0 && !versionsLoading && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-center">
                  <p className="text-sm text-rose-800">{versionsError}</p>
                  <button
                    type="button"
                    onClick={reloadVersions}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                  >
                    <SlIcon library="lucide" name="refresh-cw" style={{ fontSize: '12px' }} />
                    Retry
                  </button>
                </div>
              )}

              {!versionsLoading && !versionsError && sortedVersions.length === 0 && (
                <div className="px-4 py-10 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                    <SlIcon
                      library="lucide"
                      name="history"
                      className="text-slate-400"
                      style={{ fontSize: '22px' }}
                    />
                  </div>
                  <p className="text-sm font-medium text-slate-700">No versions yet</p>
                </div>
              )}

              {sortedVersions.map((v, i) => {
                const fields = v?.fields || {};
                const isVideo = !!fields.videoUrls;
                return (
                  <div
                    key={`${v?.version ?? i}`}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="flex items-start gap-3">
                      <MediaThumb
                        src={isVideo ? fields.videoUrls : fields.imgUrls}
                        isVideo={isVideo}
                        alt={fields.creativeName || 'version preview'}
                        className="h-16 w-16"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">
                            Version {v?.version ?? '—'}
                          </span>
                          {v?.isCurrent && (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {v?.userName || 'Unknown'}
                          {v?.userType ? <span className="capitalize"> · {v.userType}</span> : null}
                          {' · '}
                          {formatVersionTime(v?.createdAt)}
                        </p>
                        {(v?.creativeType || v?.subType) && (
                          <p className="mt-0.5 text-[11px] text-slate-400">
                            {[v?.creativeType, v?.subType].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                    {v?.summary && (
                      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-700">
                        {v.summary}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {zoom && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeZoom();
          }}
        >
          <button
            type="button"
            onClick={closeZoom}
            aria-label="Close preview"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '20px' }} />
          </button>
          {zoom.isVideo ? (
            <video src={zoom.url} controls autoPlay className="max-h-[88vh] max-w-full rounded-lg" />
          ) : (
            <img
              src={zoom.url}
              alt="preview"
              className="max-h-[88vh] max-w-full rounded-lg object-contain"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DetailDrawer;
